/**
 * JWT token storage helpers.
 *
 * Per Stage 3 decision (2026-05-22), we store the gqlauth JWT in
 * localStorage. The admin only loads trusted code we ship (no third-party
 * scripts, no rendering of user-supplied HTML in dangerouslySetInnerHTML
 * outside Tiptap's controlled sanitisation), so the XSS surface is small
 * enough that localStorage is acceptable for the committee admin.
 *
 * If we ever need to harden against XSS further, swap this module for an
 * httpOnly cookie + refresh-token flow without touching call-sites.
 */

const TOKEN_KEY = "krcl.admin.jwt";
const USER_KEY = "krcl.admin.user";

export interface StoredUser {
  email: string;
  role: string;
  /**
   * First name from Django User. Optional — Stage 3.1 doesn't fetch it
   * (the `me` query needs the JWT-auth middleware that lands in Stage 3.2),
   * so this stays undefined until then. The dashboard greeting falls back
   * to email-only when missing.
   */
  firstName?: string | null;
  lastName?: string | null;
}

export function getToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

export function setToken(token: string): void {
  try {
    localStorage.setItem(TOKEN_KEY, token);
  } catch {
    /* storage unavailable (private browsing, etc.) — fall back to in-memory only */
  }
}

export function getStoredUser(): StoredUser | null {
  try {
    const raw = localStorage.getItem(USER_KEY);
    return raw ? (JSON.parse(raw) as StoredUser) : null;
  } catch {
    return null;
  }
}

export function setStoredUser(user: StoredUser): void {
  try {
    localStorage.setItem(USER_KEY, JSON.stringify(user));
  } catch {
    /* ignore */
  }
}

export function clearAuth(): void {
  try {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
  } catch {
    /* ignore */
  }
}
